/**
 * Nombres de colecciones Firestore usados por el módulo RRHH.
 * Preferir importar desde aquí en código nuevo para evitar strings duplicados y alinear consultas con `.where()` / `limit()`.
 */

/** Colección raíz de empleados RRHH (`docId` = employeeId). */
export const RRHH_EMPLOYEES_COLLECTION = 'rrhh_employees';

/** Historial de cambios en ficha empleado (`where('employeeId', '==', id)` + `limit`). */
export const RRHH_EMPLOYEE_HISTORY_COLLECTION = 'rrhh_employee_history';

/**
 * Nombre lógico de colección para `RrhhApiService` (Firestore: `rrhh_` + este valor).
 * Asignaciones empleado ↔ evento.
 */
export const RRHH_API_RELATIONSHIPS_EMPLOYEES_EVENTS = 'relationships-employees-events';

/**
 * Historial de asignaciones empleado ↔ evento (`RrhhApiService`).
 */
export const RRHH_API_RELATIONSHIPS_EMPLOYEES_EVENTS_HISTORY = 'relationships-employees-events-history';

/**
 * Personal de eventos (`RrhhApiService` → Firestore `rrhh_employeesevents`).
 */
export const RRHH_API_EMPLOYEES_EVENTS = 'employeesevents';

/** Liquidaciones eventuales (`RrhhApiService` → `rrhh_eventual_liquidations`). */
export const RRHH_API_EVENTUAL_LIQUIDATIONS = 'eventual_liquidations';

/** Checklists de devolución EPP al dar de baja (`RrhhApiService` → `rrhh_epp_return_checklists`). */
export const RRHH_API_EPP_RETURN_CHECKLISTS = 'epp_return_checklists';

/** Flota de vehículos RRHH. */
export const RRHH_FLOTA_VEHICULOS_COLLECTION = 'rrhh_flota_vehiculos';

/** Documentos asociados a cada vehículo (subcolección bajo `rrhh_flota_vehiculos/{vehiculoId}`). */
export const RRHH_FLOTA_VEHICULOS_DOCS_SUBCOLLECTION = 'documentos';

/** Observaciones asociadas a cada vehículo (subcolección bajo `rrhh_flota_vehiculos/{vehiculoId}`). */
export const RRHH_FLOTA_VEHICULOS_OBSERVACIONES_SUBCOLLECTION = 'observaciones';

/** Siniestros asociados a cada vehículo (subcolección bajo `rrhh_flota_vehiculos/{vehiculoId}`). */
export const RRHH_FLOTA_VEHICULOS_SINIESTROS_SUBCOLLECTION = 'siniestros';

/** Eventos del vehículo: baja, alta, etc. (subcolección bajo `rrhh_flota_vehiculos/{vehiculoId}`). */
export const RRHH_FLOTA_VEHICULOS_EVENTOS_SUBCOLLECTION = 'eventos';

/** Colección global de eventos (calendario corporativo; sin prefijo `rrhh_`). */
export const GLOBAL_EVENTS_COLLECTION = 'events';

/** Valor hora por CCT/categoría (`RrhhApiService` → `rrhh_hour_values`). */
export const RRHH_API_HOUR_VALUES = 'hour_values';

/** Descuentos judiciales (`RrhhApiService` → `rrhh_judicial_deductions`). */
export const RRHH_API_JUDICIAL_DEDUCTIONS = 'judicial_deductions';

/** Reuniones y recordatorios (`RrhhApiService` → `rrhh_meetings`). */
export const RRHH_API_MEETINGS = 'meetings';

/** Eventos manuales de agenda RRHH (`RrhhApiService` → `rrhh_agenda_events`). */
export const RRHH_API_AGENDA_EVENTS = 'agenda_events';

/** Notificaciones RRHH (`RrhhApiService` → `rrhh_notifications`). */
export const RRHH_API_NOTIFICATIONS = 'notifications';

/** Catálogos de organización (`RrhhApiService`, prefijo `rrhh_`). */
export const RRHH_API_ORG_CCT = 'cct';
export const RRHH_API_ORG_CATEGORY = 'category';
export const RRHH_API_ORG_DEPARTMENT = 'department';
export const RRHH_API_ORG_SECTOR = 'sector';
export const RRHH_API_ORG_POSITION = 'position';
export const RRHH_API_ORG_DOCUMENT_TYPE = 'document_type';

/** Liquidaciones por período y convenio (`RrhhApiService` → `rrhh_payroll_liquidations`). */
export const RRHH_API_PAYROLL_LIQUIDATIONS = 'payroll_liquidations';

/** Configuración de convenios para liquidación (`RrhhApiService` → `rrhh_payroll_convenios`). */
export const RRHH_API_PAYROLL_CONVENIOS = 'payroll_convenios';

/** Conceptos de liquidación por convenio (`RrhhApiService` → `rrhh_payroll_concepts`). */
export const RRHH_API_PAYROLL_CONCEPTS = 'payroll_concepts';

/** Planillas generadas en «Generar Novedades de Liquidación» (`RrhhApiService` → `rrhh_payroll_generated_news`). */
export const RRHH_API_PAYROLL_GENERATED_NEWS = 'payroll_generated_news';

/** Sueldo básico por convenio + categoría laboral + período (`RrhhApiService` → `rrhh_payroll_category_salaries`). */
export const RRHH_API_PAYROLL_CATEGORY_SALARIES = 'payroll_category_salaries';

/** Adicionales por convenio + categoría laboral + período (`RrhhApiService` → `rrhh_payroll_additional_payments`). */
export const RRHH_API_PAYROLL_ADDITIONAL_PAYMENTS = 'payroll_additional_payments';

/** No remunerativos por convenio + categoría laboral + período (`RrhhApiService` → `rrhh_payroll_non_remunerative_payments`). */
export const RRHH_API_PAYROLL_NON_REMUNERATIVE_PAYMENTS = 'payroll_non_remunerative_payments';

/** Remunerativos fijos por convenio + categoría laboral + período (`RrhhApiService` → `rrhh_payroll_remunerative_payments`). */
export const RRHH_API_PAYROLL_REMUNERATIVE_PAYMENTS = 'payroll_remunerative_payments';

/** Descuentos judiciales por empleado (`RrhhApiService` → `rrhh_payroll_judicial_deductions`). */
export const RRHH_API_PAYROLL_JUDICIAL_DEDUCTIONS = 'payroll_judicial_deductions';

/** Adicionales en efectivo por empleado (`RrhhApiService` → `rrhh_payroll_cash_additionals`). */
export const RRHH_API_PAYROLL_CASH_ADDITIONALS = 'payroll_cash_additionals';

/** Liquidaciones de pago en efectivo (comprobante aparte del recibo de haberes). */
export const RRHH_API_PAYROLL_CASH_LIQUIDATIONS = 'payroll_cash_liquidations';

/** Planilla FATIGA-KM (horas petroleros por mes) (`RrhhApiService` → `rrhh_payroll_fatiga_km`). */
export const RRHH_API_PAYROLL_FATIGA_KM = 'payroll_fatiga_km';

/** Recibos de sueldo (`RrhhApiService` → `rrhh_payslip`). */
export const RRHH_API_PAYSLIP = 'payslip';

/** Configuración de haberes por CCT/categoría (`RrhhApiService` → `rrhh_salary_config`). */
export const RRHH_API_SALARY_CONFIG = 'salary_config';

/** Novedades / calendario (vacaciones, feriados, etc.). Consultas típicas: `where('type', 'in', ...)`, `where('employeeId', '==', id)`. */
export const RRHH_LATESTNEWS_COLLECTION = 'rrhh_latestnews';

/** Adelantos de sueldo. */
export const RRHH_SALARY_ADVANCE_COLLECTION = 'rrhh_salary_advance';

/** Observaciones por empleado. */
export const RRHH_EMPLOYEE_OBSERVATIONS_COLLECTION = 'rrhh_employee_observations';

/** Marcas de error en filas del histórico de vacaciones tomadas (solo modal histórico). */
export const RRHH_VACATION_HISTORY_ERRORS_COLLECTION = 'rrhh_vacation_history_errors';

/** Evaluaciones de desempeño. */
export const RRHH_EMPLOYEE_PERFORMANCE_EVALUATIONS_COLLECTION = 'rrhh_employee_performance_evaluations';

/** Solicitudes genéricas RRHH. */
export const RRHH_REQUESTS_COLLECTION = 'rrhh_requests';

/** Auditoría de acciones (altas/bajas, etc.). */
export const RRHH_AUDIT_LOG_COLLECTION = 'rrhh_audit_log';

/** Devoluciones de expediente médico (HSE). Consultas: `where('employeeId', '==', id)`. */
export const EXPEDIENTES_DEVOLUCIONES_COLLECTION = 'expedientes_devoluciones';

/** Sugerencias BPM al personal (`where('employeeId', '==', id)`). */
export const RRHH_EMPLOYEE_BPM_SUGGESTIONS_COLLECTION = 'rrhh_employee_bpm_suggestions';

/** Usuarios Firebase Auth / perfil (consultas: `where('email', '==', ...)`, `limit(1)`). */
export const USERS_COLLECTION = 'users';

/** Nombre de la colección de configuración de notificaciones por usuario (documentId = userId). */
export const CONFIG_NOTIFIC_COLLECTION = 'config_notific';

/** Entregas EPP (documentos en `rrhh_epp_deliveries`). */
export const RRHH_EPP_DELIVERIES_COLLECTION = 'rrhh_epp_deliveries';

/** Nombre lógico para `RrhhApiService` (equivale a `RRHH_EPP_DELIVERIES_COLLECTION` sin prefijo `rrhh_`). */
export const RRHH_API_EPP_DELIVERIES = 'epp_deliveries';

/** Flujos EPP por etapa (PPE). */
export const RRHH_PPE_FLOWS_COLLECTION = 'rrhh_ppe-flows';

/** Auditoría de flujos EPP (`epp-flows-audit` → prefijo `rrhh_`). */
export const RRHH_EPP_FLOWS_AUDIT_COLLECTION = 'rrhh_epp-flows-audit';

/** Colección legacy pre-migración (`scripts/migrate-ppe-flows.ts`). */
export const LEGACY_PPE_FLOWS_COLLECTION = 'ppe-flows';

/** Staff / perfiles profesionales. */
export const RRHH_STAFF_COLLECTION = 'rrhh_staff';

/** Nombre lógico staff para `RrhhApiService` (equivale a `RRHH_STAFF_COLLECTION` sin prefijo `rrhh_`). */
export const RRHH_API_STAFF = 'staff';

/** Vacaciones (`RrhhApiService` → `rrhh_vacations`). */
export const RRHH_API_VACATIONS = 'vacations';

/** Horarios laborales (`RrhhApiService` → `rrhh_work_schedules`). */
export const RRHH_API_WORK_SCHEDULES = 'work_schedules';

/** Capacitaciones (`RrhhApiService` → `rrhh_trainings`). */
export const RRHH_API_TRAININGS = 'trainings';

/** Tipos de capacitación — catálogo configuración (`RrhhApiService` → `rrhh_training_types`). */
export const RRHH_API_TRAINING_TYPES = 'training_types';

/** Requisitos de capacitación por puesto (`RrhhApiService` → `rrhh_training_position_requirements`). */
export const RRHH_API_TRAINING_POSITION_REQUIREMENTS = 'training_position_requirements';

/** Auditoría de cambios en requisitos por puesto (`RrhhApiService` → `rrhh_training_position_requirement_audit`). */
export const RRHH_API_TRAINING_POSITION_REQUIREMENT_AUDIT = 'training_position_requirement_audit';

/** Auditoría transversal del módulo Capacitaciones (Historial global). */
export const RRHH_API_TRAINING_ACTIVITY_AUDIT = 'training_activity_audit';

/** Planificación anual por tipo de capacitación (`RrhhApiService` → `rrhh_training_annual_plans`). */
export const RRHH_API_TRAINING_ANNUAL_PLANS = 'training_annual_plans';

/** Sesiones / capacitaciones programadas (`RrhhApiService` → `rrhh_training_scheduled_sessions`). */
export const RRHH_API_TRAINING_SCHEDULED_SESSIONS = 'training_scheduled_sessions';

/** Actas de capacitación (`RrhhApiService` → `rrhh_training_actas`). */
export const RRHH_API_TRAINING_ACTAS = 'training_actas';

/** Control horario / marcaciones (`RrhhApiService` → `rrhh_time_control`). */
export const RRHH_API_TIME_CONTROL = 'time_control';

/** Política global de control horario (documento único `rrhhConfig/timeControlPolicy`). */
export const RRHH_CONFIG_TIME_CONTROL_POLICY_DOC = 'rrhhConfig/timeControlPolicy';

/** Auditoría de subidas de documentos (panel de auditoría). */
export const RRHH_DOCUMENT_AUDITS_COLLECTION = 'rrhh_document_audits';

/** Historial de sincronización de dispositivos (consultas con `where` + `orderBy` + `limit`). */
export const RRHH_DEVICE_SYNC_HISTORY_COLLECTION = 'rrhh_device_sync_history';

/** Avisos globales / por empleado en la app (consultas con `where` + `orderBy` + `limit`). */
export const RRHH_APP_ALERTS_COLLECTION = 'rrhh_app_alerts';

/** Control documental — empresa. */
export const RRHH_CONTROL_DOCUMENTAL_EMPRESA_COLLECTION = 'rrhh_controldocumental_empresa';

/** Control documental — seguridad e higiene. */
export const RRHH_CONTROL_DOCUMENTAL_SEGURIDAD_HIGIENE_COLLECTION =
  'rrhh_controldocumental_seguridad_higiene';

/** Control documental — médico laboral. */
export const RRHH_CONTROL_DOCUMENTAL_MEDICO_LABORAL_COLLECTION = 'rrhh_controldocumental_medico_laboral';

/** Control documental — buenas prácticas. */
export const RRHH_CONTROL_DOCUMENTAL_BUENAS_PRACTICAS_COLLECTION = 'rrhh_controldocumental_buenas_practicas';

/** Diagnóstico técnico sanitizado de comparación documental (sin archivos, emails ni datos médicos). */
export const RRHH_DOCUMENT_RULE_COMPARISONS_COLLECTION = 'rrhh_document_rule_comparisons';

/** Configuración y auditoría de activación gradual del control documental. */
export const RRHH_DOCUMENT_CONTROL_ROLLOUT_COLLECTION = 'rrhh_document_control_rollout';
